/**
 * useProjectDateInputs - Business Logic Hook
 *
 * Extracts all calculation and state management logic from ProjectDateInputs component.
 * This hook provides a clean, testable API for managing project date inputs.
 *
 * Pattern Benefits:
 * - Testing: Logic can be tested independently from UI
 * - Separation: Component focuses on rendering, hook handles business logic
 * - Reusability: Pattern can be applied to other input builders
 * - Maintainability: Reduces component size and complexity
 *
 * @example
 * const {
 *   handlers: { handleInputChange, handlePaymentDateChange },
 *   computed: { validationErrors, totalPercentage },
 *   refs: { startingTaxYearInputRef }
 * } = useProjectDateInputs(values, onUpdate, showValidation);
 */

import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { DateBuilder } from '~/builders/project-dates/DateBuilder';
import {
  DATE_VALIDATION,
  getDefaultFiscalYearEnd,
  getDefaultPaymentDates,
  MUNICIPAL_PAYMENT_DATES,
} from '~/config/municipal-bond-date-constants';
import type { DateInputsForm, PaymentDateForm } from '~/types';

// Constants
const MIN_DISPLAY_YEAR = 2020;
const MAX_DISPLAY_YEAR = 2050;
const MIN_PAYMENT_DATES = 2;
const FISCAL_YEAR_TRANSITION_MONTH = MUNICIPAL_PAYMENT_DATES.MAY.MONTH;

// Helper functions
const createEmptyPayment = (): PaymentDateForm => ({
  date: '',
  isInterestOnly: undefined, // Neither option selected by default
  revenuePercentage: '0', // User must set this
});

const ensurePaymentDatesLength = (arr: PaymentDateForm[], minLen: number): PaymentDateForm[] => {
  const result = [...arr];
  while (result.length < minLen) {
    result.push(createEmptyPayment());
  }
  return result;
};

interface UseProjectDateInputsOptions {
  values: DateInputsForm;
  onUpdate: (values: DateInputsForm) => void;
  showValidation?: boolean;
}

export const useProjectDateInputs = ({
  values,
  onUpdate,
  showValidation = false,
}: UseProjectDateInputsOptions) => {
  // State
  const [touched, setTouched] = useState<Set<string>>(new Set());

  // Refs
  const hasInitialized = useRef(false);
  const previousYearRef = useRef<string>('');
  const startingTaxYearInputRef = useRef<HTMLInputElement>(null);

  /**
   * Generate payment dates based on tax collection schedule
   * Summer: 11/01/[year], 05/01/[year+1]
   * Winter: 05/01/[year+1], 11/01/[year+1]
   *
   * Preserves existing revenuePercentage and isInterestOnly values by POSITION (first/second)
   * not by month, so percentages stay in place when switching between schedules.
   *
   * Default values: User must set both revenuePercentage (starts at '0') and isInterestOnly (starts as undefined)
   */
  const generatePaymentDatesFromSchedule = useCallback(
    (
      year: number,
      schedule: 'summer' | 'winter',
      existingPaymentDates?: PaymentDateForm[]
    ): PaymentDateForm[] => {
      // Get existing values by position (first payment, second payment)
      // This ensures percentages don't "jump around" when switching schedules
      const firstPaymentValues = existingPaymentDates?.[0];
      const secondPaymentValues = existingPaymentDates?.[1];

      if (schedule === 'summer') {
        // Summer: November is first, May is second
        const novemberDate = `${year}-11-01`;
        const mayDate = `${year + 1}-05-01`;
        return [
          {
            date: novemberDate,
            isInterestOnly: firstPaymentValues?.isInterestOnly ?? undefined, // User must select
            revenuePercentage: firstPaymentValues?.revenuePercentage ?? '0', // User must set
          },
          {
            date: mayDate,
            isInterestOnly: secondPaymentValues?.isInterestOnly ?? undefined, // User must select
            revenuePercentage: secondPaymentValues?.revenuePercentage ?? '0', // User must set
          },
        ];
      } else {
        // Winter: May is first, November is second
        const mayDate = `${year + 1}-05-01`;
        const novemberDate = `${year + 1}-11-01`;
        return [
          {
            date: mayDate,
            isInterestOnly: firstPaymentValues?.isInterestOnly ?? undefined, // User must select
            revenuePercentage: firstPaymentValues?.revenuePercentage ?? '0', // User must set
          },
          {
            date: novemberDate,
            isInterestOnly: secondPaymentValues?.isInterestOnly ?? undefined, // User must select
            revenuePercentage: secondPaymentValues?.revenuePercentage ?? '0', // User must set
          },
        ];
      }
    },
    []
  );

  /**
   * Calculate fiscal year end and payment dates from a tax year
   * Preserves user's month/day preferences when updating years
   * Uses tax collection schedule to generate payment dates and fiscal year end if set
   */
  const calculateDatesFromYear = useCallback(
    (
      year: number,
      currentPaymentDates: PaymentDateForm[],
      taxCollectionSchedule?: 'summer' | 'winter'
    ) => {
      // Calculate fiscal year end based on tax collection schedule (defaults to summer if not provided)
      const fiscalYearEnd = getDefaultFiscalYearEnd(year, taxCollectionSchedule);
      const updates: Partial<DateInputsForm> = {
        fiscalYearEnd,
      };

      // If tax collection schedule is set, use it to generate payment dates
      // Preserve existing revenuePercentage and isInterestOnly values
      if (taxCollectionSchedule) {
        updates.paymentDates = generatePaymentDatesFromSchedule(
          year,
          taxCollectionSchedule,
          currentPaymentDates
        );
        return updates;
      }

      // Check if payment dates exist and have valid dates
      const hasValidPaymentDates =
        currentPaymentDates.length > 0 &&
        currentPaymentDates.some(
          (p) => p.date && p.date.trim() !== '' && p.date.match(/^\d{4}-\d{2}-\d{2}$/)
        );

      if (hasValidPaymentDates) {
        // Preserve month/day but update year based on starting tax year
        const updatedPaymentDates = currentPaymentDates.map((payment) => {
          if (payment.date?.match(/^\d{4}-\d{2}-\d{2}$/)) {
            // Extract month and day from existing date
            const [, month, day] = payment.date.split('-');
            // Determine fiscal year based on month
            // If month is Jan-May (FISCAL_YEAR_TRANSITION_MONTH), it's in the next fiscal year
            // If month is Jun-Dec, it's in the current fiscal year
            const paymentMonth = parseInt(month, 10);
            const paymentYear = paymentMonth <= FISCAL_YEAR_TRANSITION_MONTH ? year + 1 : year;
            return {
              ...payment,
              date: `${paymentYear}-${month}-${day}`,
            };
          }
          // If date is invalid, preserve the payment object as-is
          return payment;
        });

        // Ensure we have at least MIN_PAYMENT_DATES
        if (updatedPaymentDates.length < MIN_PAYMENT_DATES) {
          // Use default payment dates from constants to fill missing slots
          // Only populate dates - leave payment types and revenue percentages empty for user input
          const defaultDates = getDefaultPaymentDates(year);
          while (updatedPaymentDates.length < MIN_PAYMENT_DATES) {
            const defaultIndex = updatedPaymentDates.length;
            const defaultDate = defaultDates[defaultIndex];
            updatedPaymentDates.push({
              date: defaultDate?.date || '',
              isInterestOnly: undefined, // User must select
              revenuePercentage: '0', // User must set
            });
          }
        }

        updates.paymentDates = updatedPaymentDates;
      } else {
        // Use default payment dates from constants (Nov 1 and May 1)
        // Only populate dates - leave payment types and revenue percentages empty for user input
        const defaultDates = getDefaultPaymentDates(year);
        updates.paymentDates = defaultDates.map((d) => ({
          date: d.date,
          isInterestOnly: undefined, // User must select
          revenuePercentage: '0', // User must set
        }));
      }

      return updates;
    },
    [generatePaymentDatesFromSchedule]
  );

  // Auto-populate starting tax year with next calendar year if empty (only on mount)
  // Also set default tax collection schedule to "summer"
  // Intentionally runs only once on mount - using ref to prevent re-runs
  // Only auto-populate if ALL critical fields are empty (indicates fresh form, not reloaded data)
  useEffect(() => {
    const isCompletelyEmpty =
      (!values.startingTaxYear || values.startingTaxYear.trim() === '') &&
      (!values.fiscalYearEnd || values.fiscalYearEnd.trim() === '') &&
      (!values.paymentDates ||
        values.paymentDates.length === 0 ||
        values.paymentDates.every((pd) => !pd.date));

    if (!hasInitialized.current && isCompletelyEmpty) {
      hasInitialized.current = true;
      const nextYear = new Date().getFullYear() + 1;
      const defaultSchedule = 'summer' as const;
      const updates = calculateDatesFromYear(nextYear, values.paymentDates || [], defaultSchedule);

      onUpdate({
        ...values,
        startingTaxYear: nextYear.toString(),
        taxCollectionSchedule: defaultSchedule,
        ...updates,
      });
      previousYearRef.current = nextYear.toString();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // Intentionally empty: Only run on mount, not when values change
  }, [calculateDatesFromYear, onUpdate, values]);

  // Auto-focus on Starting Tax Year field when component mounts
  useEffect(() => {
    const timeout = setTimeout(() => {
      startingTaxYearInputRef.current?.focus();
    }, 100);

    return () => clearTimeout(timeout);
  }, []);

  // Auto-calculate fiscal year end and payment dates when startingTaxYear changes
  // Uses ref to track previous year to avoid unnecessary updates
  // calculateDatesFromYear and onUpdate are stable (useCallback), so safe to omit from deps
  useEffect(() => {
    const year = parseInt(values.startingTaxYear, 10);
    const isValidYear =
      !Number.isNaN(year) &&
      year >= DATE_VALIDATION.MIN_TAX_YEAR &&
      year <= DATE_VALIDATION.MAX_TAX_YEAR;
    const yearChanged = previousYearRef.current !== values.startingTaxYear;

    if (isValidYear && yearChanged && values.startingTaxYear.trim() !== '') {
      // Skip update on initial mount if data is already complete (was loaded from store)
      const hasCompleteData =
        values.fiscalYearEnd &&
        values.paymentDates &&
        values.paymentDates.length >= MIN_PAYMENT_DATES &&
        values.paymentDates.some(
          (pd) => pd.revenuePercentage && pd.revenuePercentage.trim() !== ''
        );

      // If this is the first load (previousYear is empty) and we have complete data, skip the update
      if (previousYearRef.current === '' && hasCompleteData) {
        previousYearRef.current = values.startingTaxYear;
        return;
      }

      previousYearRef.current = values.startingTaxYear;

      // Update payment dates based on tax collection schedule if set, otherwise preserve existing dates
      const updates = calculateDatesFromYear(
        year,
        values.paymentDates || [],
        values.taxCollectionSchedule
      );

      onUpdate({
        ...values,
        ...updates,
      });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
    // Only depend on startingTaxYear - calculateDatesFromYear and onUpdate are stable callbacks
    // values.paymentDates is intentionally omitted - we only want to recalculate when year changes
  }, [values.startingTaxYear, onUpdate, values, calculateDatesFromYear]);

  // Handlers
  const markTouched = useCallback((field: string) => {
    setTouched((prev) => new Set(prev).add(field));
  }, []);

  const handleInputChange = useCallback(
    (field: keyof Omit<DateInputsForm, 'paymentDates'>, value: string | 'summer' | 'winter') => {
      const updates: Partial<DateInputsForm> = {
        [field]: value,
      };

      // If tax collection schedule changes, regenerate payment dates AND fiscal year end
      // Preserve existing revenuePercentage and isInterestOnly values
      if (field === 'taxCollectionSchedule' && (value === 'summer' || value === 'winter')) {
        const year = parseInt(values.startingTaxYear, 10);
        const isValidYear =
          !Number.isNaN(year) &&
          year >= DATE_VALIDATION.MIN_TAX_YEAR &&
          year <= DATE_VALIDATION.MAX_TAX_YEAR;
        if (isValidYear && values.startingTaxYear.trim() !== '') {
          updates.paymentDates = generatePaymentDatesFromSchedule(year, value, values.paymentDates);
          updates.fiscalYearEnd = getDefaultFiscalYearEnd(year, value);
        }
      }

      onUpdate({
        ...values,
        ...updates,
      });
    },
    [values, onUpdate, generatePaymentDatesFromSchedule]
  );

  const handlePaymentDateChange = useCallback(
    (index: number, field: keyof PaymentDateForm, value: string | boolean) => {
      const baseArray = ensurePaymentDatesLength(
        values.paymentDates as PaymentDateForm[],
        Math.max(index + 1, MIN_PAYMENT_DATES)
      );
      const updatedPaymentDates = [...baseArray] as PaymentDateForm[];
      updatedPaymentDates[index] = {
        ...updatedPaymentDates[index],
        [field]: value,
      };
      onUpdate({
        ...values,
        paymentDates: updatedPaymentDates,
      });
    },
    [values, onUpdate]
  );

  const handleRemovePaymentDate = useCallback(
    (index: number) => {
      const updatedPaymentDates = values.paymentDates.filter((_, i) => i !== index);
      // Ensure minimum length
      const finalPaymentDates = ensurePaymentDatesLength(updatedPaymentDates, MIN_PAYMENT_DATES);
      onUpdate({
        ...values,
        paymentDates: finalPaymentDates,
      });
    },
    [values, onUpdate]
  );

  const handleStartingTaxYearBlur = useCallback(() => {
    markTouched('startingTaxYear');
  }, [markTouched]);

  // Computed values
  const totalPercentage = useMemo(() => {
    return values.paymentDates.reduce((sum, payment) => {
      return sum + (parseFloat(payment.revenuePercentage || '') || 0);
    }, 0);
  }, [values.paymentDates]);

  const displayPaymentDates = useMemo(() => {
    return ensurePaymentDatesLength(values.paymentDates as PaymentDateForm[], MIN_PAYMENT_DATES);
  }, [values.paymentDates]);

  // Check if all required fields have been touched
  const allFieldsTouched = useMemo(() => {
    // Check basic fields
    const basicFieldsTouched = touched.has('startingTaxYear') && touched.has('fiscalYearEnd');

    // Check if all payment dates have been touched (at least date and revenue for each)
    const paymentDatesTouched = displayPaymentDates.every((_payment, index) => {
      return touched.has(`payment-${index}-date`) && touched.has(`payment-${index}-revenue`);
    });

    return basicFieldsTouched && paymentDatesTouched;
  }, [touched, displayPaymentDates]);

  const validationErrors = useMemo(() => {
    // Use DateBuilder validation for consistency (single source of truth)
    const allErrors = DateBuilder.validate(values);

    // Only show validation errors if:
    // 1. showValidation is true (user clicked Continue), OR
    // 2. All fields in the section have been touched
    if (!showValidation && !allFieldsTouched) {
      return [];
    }

    // If showValidation is true, show all errors regardless of touched state
    if (showValidation) {
      return allErrors;
    }

    // Filter errors based on touched fields (only reached if allFieldsTouched is true)
    const filteredErrors: string[] = [];

    for (const error of allErrors) {
      // Map error messages to field names
      if (error.includes('Starting tax year')) {
        if (touched.has('startingTaxYear')) {
          filteredErrors.push(error);
        }
      } else if (error.includes('Fiscal year end')) {
        if (touched.has('fiscalYearEnd')) {
          filteredErrors.push(error);
        }
      } else if (error.includes('Payment date')) {
        // Extract payment index from error message
        const match = error.match(/Payment date (\d+)/);
        if (match) {
          const index = parseInt(match[1], 10) - 1;
          if (error.includes('is required') || error.includes('must be a valid date')) {
            if (touched.has(`payment-${index}-date`)) {
              filteredErrors.push(error);
            }
          } else if (error.includes('Revenue percentage')) {
            if (touched.has(`payment-${index}-revenue`)) {
              filteredErrors.push(error);
            }
          } else {
            // Date format validation errors
            // Show if any payment date field is touched
            const hasTouchedPaymentDate = Array.from(touched).some(
              (field) => field.startsWith(`payment-`) && field.includes('-date')
            );
            if (hasTouchedPaymentDate || touched.has('fiscalYearEnd')) {
              filteredErrors.push(error);
            }
          }
        }
      } else if (error.includes('Revenue percentages must sum')) {
        // Show percentage sum error if any revenue field is touched
        const hasTouchedRevenue = Array.from(touched).some((field) => field.includes('-revenue'));
        if (hasTouchedRevenue) {
          filteredErrors.push(error);
        }
      } else if (error.includes('At least one payment date')) {
        // Show if any payment-related field is touched
        const hasTouchedPayment = Array.from(touched).some((field) => field.startsWith('payment-'));
        if (hasTouchedPayment) {
          filteredErrors.push(error);
        }
      }
    }

    return filteredErrors;
  }, [values, touched, showValidation, allFieldsTouched]);

  const revenuePercentageStatus = useMemo(() => {
    if (totalPercentage === 0) return { color: 'gray.600', status: 'empty' };
    if (Math.abs(totalPercentage - 100) < DATE_VALIDATION.REVENUE_PERCENTAGE_TOLERANCE)
      return { color: 'green.700', status: 'complete' };
    return { color: 'red.700', status: 'incomplete' };
  }, [totalPercentage]);

  const revenuePercentageMessage = useMemo(() => {
    const diff = Math.abs(totalPercentage - 100);
    if (diff < DATE_VALIDATION.REVENUE_PERCENTAGE_TOLERANCE) return 'Complete';
    if (totalPercentage < 100) return `${(100 - totalPercentage).toFixed(1)}% remaining`;
    return `${(totalPercentage - 100).toFixed(1)}% over`;
  }, [totalPercentage]);

  return {
    // Handlers
    handlers: {
      handleInputChange,
      handlePaymentDateChange,
      handleRemovePaymentDate,
      handleStartingTaxYearBlur,
      markTouched,
    },

    // Computed values
    computed: {
      totalPercentage,
      displayPaymentDates,
      validationErrors,
      revenuePercentageStatus,
      revenuePercentageMessage,
      allFieldsTouched,
    },

    // Refs
    refs: {
      startingTaxYearInputRef,
    },

    // Constants
    constants: {
      MIN_DISPLAY_YEAR,
      MAX_DISPLAY_YEAR,
      MIN_PAYMENT_DATES,
    },
  };
};
